import React, { useState, useMemo, useCallback } from "react";
import {
  createStyles,
  Grid,
  makeStyles,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  useTheme,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import DeleteIcon from "@material-ui/icons/Delete";

import WithLoading from "../../../../utils/WithLoading";
import CardCommon from "../../../../components/card/CardCommon";
import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";
import IntegrationCreateModal from "./IntegrationCreateModal";
import { deleteIntegrationConfig } from "../../../../services/locationApp/integrationsService";
import { useRouteMatch } from "react-router-dom";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";

export interface IntegrationData {
  accountId?: string;
  hubriseAccountId?: string;
  hubriseAccountName?: string;
  accountName?: string;
  [key: string]: any;
}

export interface IntegrationItem {
  id?: string | number;
  type?: string;
  name?: string;
  data?: IntegrationData;
  [key: string]: any;
}

export interface IntegrationsInfoNodeProps {
  isLoading: boolean;
  integrations: IntegrationItem[];
  getAllIntegrationConfig: () => void;
  getAllIntegrationsPerLocation?: () => void;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    headerCard: {
      marginTop: "20px",
      height: "44px",
      display: "flex",
      alignItems: "center",
    },
    headerText: {
      fontWeight: 600,
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
    },
    headerClickable: {
      paddingLeft: "16px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
    },
    centeredItem: {
      display: "flex",
      alignItems: "center",
      height: "100%",
    },
    rightHeaderItem: {
      paddingRight: "16px",
    },
    rowCard: {
      padding: "4px 0",
      marginTop: 6,
      transition: "box-shadow 0.2s ease-in-out",
      "&:hover": {
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
      },
    },
    cellText: {
      fontSize: "0.875rem",
      color: theme.palette.text.secondary,
    },
    leftCell: {
      paddingLeft: "16px",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
    },
    rightCell: {
      paddingRight: "16px",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "8px",
    },
    emptyContainer: {
      padding: "32px",
      textAlign: "center",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "40px 0",
    },
    deleteButton: {
      padding: "4px",
      "&:hover": {
        backgroundColor: "rgba(255, 0, 0, 0.08)",
      },
    },
  }),
);

// Helper function to extract account name dynamically across providers
const getAccountName = (item: IntegrationItem): string => {
  return (
    item.data?.hubriseAccountName ||
    item.data?.accountName ||
    item.data?.accountId ||
    item.name ||
    "-"
  );
};

// Helper function to extract a stable React key
const getAccountKey = (
  item: IntegrationItem,
  index: number,
): string | number => {
  return (
    item.id || item.data?.hubriseAccountId || item.data?.accountId || index
  );
};

const IntegrationsInfoNode: React.FC<IntegrationsInfoNodeProps> = ({
  integrations = [],
  getAllIntegrationConfig,
  getAllIntegrationsPerLocation,
}) => {
  const classes = useStyles();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingTypes, setDeletingTypes] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const match: any = useRouteMatch();
  const locationId = (match.params as any).locationId;

  // Extract existing integration types
  const existingTypes = useMemo(() => {
    return integrations.map((item) => item.type?.toLowerCase()).filter(Boolean);
  }, [integrations]);

  const toggleSort = useCallback(() => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  }, []);

  const handleCreateIntegration = () => {
    setOpenDialog(true);
  };
  const handleCreateNewIntegration = async (selectedType: string) => {
    try {
      setIsCreating(true);

      // Here you would make your API call to create the integration
      // const response = await createIntegration(locationId, selectedType);
      console.log(`Creating ${selectedType} integration...`);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      getAllIntegrationConfig();
      // // Refresh the integrations list after successful creation
      // if (getAllIntegrationsPerLocation) {
      //   await getAllIntegrationsPerLocation();
      // }

      // Close the dialog
      setOpenDialog(false);
    } catch (error) {
      console.error("Failed to create integration:", error);
      // You might want to show an error message here
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteIntegration = async (type: string) => {
    try {
      // Add type to deleting set to show loading state
      setDeletingTypes((prev) => new Set(prev).add(type));

      const response = await deleteIntegrationConfig(locationId, type);

      if (response.status === 200) {
        setSuccessMessage(
          `Integration of type "${type}" deleted successfully.`,
        );

        // Refresh the integrations list after deletion
        if (getAllIntegrationsPerLocation) {
          await getAllIntegrationsPerLocation();
        }
        // // Also refresh config if needed
        // getAllIntegrationConfig();
      } else {
        setError(`Failed to delete integration of type "${type}".`);
      }
    } catch (error) {
      console.error("Failed to delete integration:", error);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      // Remove type from deleting set
      setDeletingTypes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(type);
        return newSet;
      });
    }
  };

  // Memoized sorting for render optimization
  const sortedIntegrations = useMemo(() => {
    if (!Array.isArray(integrations)) return [];

    return [...integrations].sort((a, b) => {
      const nameA = getAccountName(a);
      const nameB = getAccountName(b);
      return sortOrder === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [integrations, sortOrder]);
  const theme: CustomTheme = useTheme();
  return (
    <Box width="100%">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
          marginTop: "16px",
        }}
      >
        <ButtonCommon
          onClick={handleCreateIntegration}
          variant="contained"
          style={{
            fontSize: 11,
            width: "160px",
            marginRight: "14px",
          }}
          color={buttonColors.CREATE_BUTTON_COLOR}
        >
          {"Create Integration"}
        </ButtonCommon>
      </div>

      {/* Table Header */}
      <CardCommon
        backgroundColor="table_header_background"
        className={classes.headerCard}
      >
        <Grid container alignItems="center">
          <Grid item xs={2} className={classes.centeredItem}>
            <Typography
              className={`${classes.headerText} ${classes.headerClickable}`}
              align="left"
              onClick={toggleSort}
            >
              Account Name
              {sortOrder === "asc" ? (
                <ArrowDropUpIcon fontSize="small" />
              ) : (
                <ArrowDropDownIcon fontSize="small" />
              )}
            </Typography>
          </Grid>
           <Grid
            item
            xs={2}
            className={`${classes.centeredItem}`}
          >
            <Typography
              className={classes.headerText}
              align="left"
              style={{ width: "100%" }}
            >
              Location Name
            </Typography>
          </Grid>
           <Grid
            item
            xs={2}
            className={`${classes.centeredItem}`}
          >
            <Typography
              className={classes.headerText}
              align="left"
              style={{ width: "100%" }}
            >
              Account Id
            </Typography>
          </Grid>
           <Grid
            item
            xs={2}
            className={`${classes.centeredItem} ${classes.rightHeaderItem}`}
          >
            <Typography
              className={classes.headerText}
              align="left"
              style={{ width: "100%" }}
            >
             Location Id
            </Typography>
          </Grid>
          <Grid
            item
            xs={4}
            className={`${classes.centeredItem} ${classes.rightHeaderItem}`}
          >
            <Typography
              className={classes.headerText}
              align="right"
              style={{ width: "100%" }}
            >
              Type
            </Typography>
          </Grid>
        </Grid>
      </CardCommon>
      {console.log("sortedIntegrations", sortedIntegrations)}
      {/* Table Body */}
      <Grid container direction="column">
        <Grid item xs={12}>
          {sortedIntegrations.length > 0 ? (
            sortedIntegrations.map((item: IntegrationItem, index: number) => {
              const accountName = getAccountName(item);
              const hubriseLocationName = item?.data?.hubriseLocationName || "-";
              const hubriseAccountId = item?.data?.hubriseAccountId || "-";
              const hubriseLocationId = item?.data?.hubriseLocationId || "-";
              const rowKey = getAccountKey(item, index);
              const isDeleting = deletingTypes.has(item.type || "");

              return (
                <CardCommon
                  key={rowKey}
                  backgroundColor="entity_background"
                  className={classes.rowCard}
                >
                  <Grid container alignItems="center">
                    <Grid item xs={2}>
                      <Typography
                        className={`${classes.cellText} ${classes.leftCell}`}
                        align="left"
                      >
                        {accountName}
                      </Typography>
                    </Grid>
                      <Grid item xs={2}>
                      <Typography
                        className={`${classes.cellText} ${classes.leftCell}`}
                        align="left"
                      >
                        {hubriseLocationName || "-"}
                      </Typography>
                    </Grid>
                      <Grid item xs={2}>
                      <Typography
                        className={`${classes.cellText} ${classes.leftCell}`}
                        align="left"
                      >
                        {hubriseAccountId || "-"}
                      </Typography>
                    </Grid>
                      <Grid item xs={2}>
                      <Typography
                        className={`${classes.cellText} ${classes.leftCell}`}
                        align="left"
                      >
                        {hubriseLocationId || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} className={classes.rightCell}>
                      <Typography className={classes.cellText} align="right">
                        {item.type || "-"}
                      </Typography>
                      <ButtonCommon
                        size="small"
                        className={classes.deleteButton}
                        onClick={() => handleDeleteIntegration(item.type || "")}
                        disabled={isDeleting || item.type !== "hubrise"}
                        style={{
                          backgroundColor: theme.palette.custom.red.main,
                          padding: "0px",
                          minWidth: "32px",
                          height: "32px",
                          borderRadius: "10px",
                        }}
                      >
                        {isDeleting ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon
                            style={{ color: "white" }}
                            fontSize="small"
                          />
                        )}
                      </ButtonCommon>
                    </Grid>
                  </Grid>
                </CardCommon>
              );
            })
          ) : (
            <CardCommon
              backgroundColor="entity_background"
              className={classes.emptyContainer}
            >
              <Typography variant="body2" color="textSecondary">
                No integrations found.
              </Typography>
            </CardCommon>
          )}
        </Grid>
      </Grid>

      <IntegrationCreateModal
        isLoading={isCreating}
        setOpenDialog={setOpenDialog}
        openDialog={openDialog}
        handleCreateIntegration={handleCreateNewIntegration}
        existingTypes={existingTypes}
      />

      <DefaultAlert
        open={!!successMessage}
        handleClose={() => setSuccessMessage("")}
        message={successMessage}
        severity="success"
      />

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </Box>
  );
};

export default WithLoading(IntegrationsInfoNode);
